import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [focused, setFocused] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setMounted(true), 60);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username, password });
      login(res.data.user, res.data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid username or password.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      fontFamily: "'Poppins', sans-serif",
      background: '#f8f7ff',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes floatA {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(-18px) rotate(3deg); }
        }
        @keyframes floatB {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(14px) rotate(-2deg); }
        }
        @keyframes floatC {
          0%,100% { transform: translate(0,0); }
          33%      { transform: translate(8px,-12px); }
          66%      { transform: translate(-6px,8px); }
        }
        @keyframes pulse {
          0%,100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.04); }
        }
        @keyframes slideError {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .nova-input {
          width: 100%;
          border: 1.5px solid #e5e3f0;
          border-radius: 12px;
          padding: 14px 46px 14px 46px;
          font-size: 15px;
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
          color: #1a1035;
          background: #ffffff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .nova-input:focus {
          border-color: #7c3aed;
          box-shadow: 0 0 0 4px rgba(124,58,237,0.08);
        }
        .nova-input::placeholder { color: #b8b3cc; }
        .nova-input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #fff inset !important;
          -webkit-text-fill-color: #1a1035 !important;
        }
        .login-btn {
          width: 100%;
          background: #7c3aed;
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 15px;
          font-size: 15px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          cursor: pointer;
          letter-spacing: 0.3px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          box-shadow: 0 4px 16px rgba(124,58,237,0.25);
        }
        .login-btn:hover:not(:disabled) {
          background: #6d28d9;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(124,58,237,0.32);
        }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        .login-btn:disabled { opacity: 0.65; cursor: not-allowed; }

        .eye-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #b8b3cc;
          display: flex;
          align-items: center;
          padding: 2px;
          transition: color 0.2s;
        }
        .eye-btn:hover { color: #7c3aed; }

        /* Responsive */
        @media (max-width: 768px) {
          .left-panel { display: none !important; }
          .right-panel {
            width: 100% !important;
            padding: 40px 24px !important;
          }
        }
        @media (max-width: 400px) {
          .right-panel { padding: 32px 18px !important; }
        }
      `}</style>

      {/* ── LEFT PANEL ─────────────────────────────── */}
      <div className="left-panel" style={{
        flex: 1,
        background: 'linear-gradient(145deg, #1e0a4a 0%, #2d1472 40%, #4c1d95 70%, #5b21b6 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Abstract geometric shapes */}
        <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
          {/* Large soft circle top-right */}
          <div style={{ position:'absolute', top:-120, right:-80, width:420, height:420, borderRadius:'50%', background:'rgba(167,139,250,0.12)', animation:'floatA 9s ease-in-out infinite' }}/>
          {/* Medium ring */}
          <div style={{ position:'absolute', top:'30%', left:-60, width:280, height:280, borderRadius:'50%', border:'1px solid rgba(196,181,253,0.15)', animation:'floatB 12s ease-in-out infinite' }}/>
          {/* Small accent circle */}
          <div style={{ position:'absolute', bottom:'25%', right:'20%', width:120, height:120, borderRadius:'50%', background:'rgba(196,181,253,0.1)', animation:'floatC 8s ease-in-out infinite' }}/>
          {/* Subtle grid lines */}
          <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.06 }}>
            <defs>
              <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#c4b5fd" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
          </svg>
          {/* Floating dots */}
          {[[14,'12%','18%'],[6,'55%','8%'],[10,'80%','35%'],[5,'25%','72%'],[8,'70%','78%']].map(([s,t,l],i)=>(
            <div key={i} style={{ position:'absolute', top:t, left:l, width:s, height:s, borderRadius:'50%', background:'rgba(196,181,253,0.4)', animation:`pulse ${3+i*0.7}s ease-in-out infinite`, animationDelay:`${i*0.5}s` }}/>
          ))}
        </div>

        {/* Logo top */}
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:50, height:50, borderRadius:12, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)' }}>
            <img src="/nova-logo.svg" style={{width:60,height:60,objectFit:'contain'}}/>
            
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:18, color:'#fff', letterSpacing:0.3 }}>Cloud</div>
              <div style={{ fontWeight:400, fontSize:11, color:'rgba(196,181,253,0.7)', letterSpacing:1.5, textTransform:'uppercase' }}>Media Drive</div>
            </div>
          </div>
        </div>

        {/* Center content */}
        <div style={{
          position: 'relative', zIndex: 1,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(24px)',
          transition: 'all 0.8s cubic-bezier(0.22,1,0.36,1) 0.2s',
        }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:24, padding:'6px 16px', marginBottom:28, backdropFilter:'blur(8px)' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#a78bfa', animation:'pulse 2s ease-in-out infinite' }}/>
            <span style={{ fontWeight:600, fontSize:11, color:'rgba(196,181,253,0.9)', letterSpacing:2, textTransform:'uppercase' }}>Media Cloud Platform</span>
          </div>

          <h1 style={{ fontWeight:800, fontSize:'clamp(32px,3.5vw,48px)', color:'#fff', lineHeight:1.1, letterSpacing:-1, marginBottom:20 }}>
            Your Media.<br/>
            <span style={{ color:'#c4b5fd' }}>One Place.</span>
          </h1>

          <p style={{ fontWeight:300, fontSize:15, color:'rgba(196,181,253,0.65)', lineHeight:1.8, maxWidth:340, marginBottom:40 }}>
            Upload, organise and stream your photoshoots, ad shoots and video content — compressed and ready for your team.
          </p>

          <div style={{ display:'flex', gap:32 }}>
            {[['3 TB','Storage'],['720p','Compressed'],['< 2min','Processing']].map(([v,l])=>(
              <div key={l}>
                <div style={{ fontWeight:800, fontSize:22, color:'#fff', letterSpacing:-0.5 }}>{v}</div>
                <div style={{ fontWeight:400, fontSize:11, color:'rgba(196,181,253,0.55)', textTransform:'uppercase', letterSpacing:1.2, marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontWeight:400, fontSize:12, color:'rgba(196,181,253,0.4)', letterSpacing:0.3 }}>
            Where your media lives. · Built by <span style={{ color:'rgba(196,181,253,0.7)', fontWeight:600 }}>WebMint Solutions</span>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ─────────────────────────────── */}
      <div className="right-panel" style={{
        width: '46%',
        minWidth: 380,
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 56px',
        position: 'relative',
        borderLeft: '1px solid #ede9fe',
      }}>
        {/* Subtle top-right accent */}
        <div style={{ position:'absolute', top:0, right:0, width:180, height:180, background:'radial-gradient(circle at top right, rgba(124,58,237,0.05) 0%, transparent 70%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:0, left:0, width:160, height:160, background:'radial-gradient(circle at bottom left, rgba(124,58,237,0.04) 0%, transparent 70%)', pointerEvents:'none' }}/>

        <div style={{
          width: '100%', maxWidth: 380,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateX(0)' : 'translateX(20px)',
          transition: 'all 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s',
        }}>
          {/* Header */}
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontWeight: 800, fontSize: 30, color: '#1a1035', letterSpacing: -0.8, marginBottom: 8, lineHeight: 1.2 }}>
              Welcome back
            </h2>
            <p style={{ fontWeight: 400, fontSize: 15, color: '#8b7fb0', lineHeight: 1.6 }}>
              Sign in to your NOVA workspace
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:22, display:'flex', alignItems:'center', gap:10, animation:'slideError 0.3s ease' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/><line x1="12" y1="8" x2="12" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="16" r="1" fill="#ef4444"/></svg>
              <span style={{ fontWeight:500, fontSize:13, color:'#dc2626' }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#4a3f6b', marginBottom:8, letterSpacing:0.1 }}>
                Username
              </label>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:15, top:'50%', transform:'translateY(-50%)', color: focused==='username'?'#7c3aed':'#c4b5fd', transition:'color 0.2s' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <input
                  className="nova-input"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onFocus={() => setFocused('username')}
                  onBlur={() => setFocused('')}
                  placeholder="Enter your username"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 28 }}>
              <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#4a3f6b', marginBottom:8, letterSpacing:0.1 }}>
                Password
              </label>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:15, top:'50%', transform:'translateY(-50%)', color: focused==='password'?'#7c3aed':'#c4b5fd', transition:'color 0.2s' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </span>
                <input
                  className="nova-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused('')}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: 46 }}
                />
                <button type="button" className="eye-btn" onClick={() => setShowPass(!showPass)}
                  style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)' }}>
                  {showPass ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.3)', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                  Signing in...
                </>
              ) : 'Sign In'}
            </button>
          </form>

          {/* Footer */}
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0edfb' }}>
            <p style={{ fontWeight:400, fontSize:12, color:'#c4b5fd', textAlign:'center', lineHeight:1.6 }}>
              Credentials are provided by your workspace admin.<br/>
              <span style={{ color:'#a78bfa', fontWeight:600 }}>Powered by WebMint Solutions</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}